<template>
  <!-- Loading Overlay -->
  <div v-if="isLoading" class="loader-container">
    <div class="text-center">
      <div class="text-secondary mb-3">Loading data</div>
      <div class="progress progress-sm">
        <div class="progress-bar progress-bar-indeterminate"></div>
      </div>
    </div>
  </div>

  <div class="page-wrapper">
    <!-- Header Card -->
    <div class="container mt-3">
      <div class="card modern-card shadow-sm h-100">
        <div class="card-header">
          <i class="pi pi-shield"></i>  
          <h3 class="card-title">{{ documentTitle }}</h3>
        </div>
        <div class="card-body">
          <div class="row align-items-center">
            <div class="col">
              <div class="d-flex align-items-center">
                <span class="me-2">Patch Tuesday</span>
                <ToggleSwitch v-model="patchTuesdayOnly" />
              </div>
            </div>
            <div class="col-auto">
              <div class="input-group">
                <span class="input-group-text bg-white">
                  <IconCalendar
                    class="icon"
                    stroke-width="2"
                    color="currentColor"
                  />
                </span>
                <select
                  class="form-select"
                  v-if="filteredSummaries.length > 0"
                  v-model="selectedMonth"
                  @change="retrieveDataForSelectedMonth"
                  aria-label="Select month"
                >
                  <option
                    v-for="summary in filteredSummaries"
                    :key="summary.ID"
                    :value="summary.ID"
                  >
                    {{ summary.ID }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Vulnerabilities Stats Card -->
    <div class="container mt-3">
      <div class="card modern-card shadow-sm h-100">
        <div class="card-header">
          <IconChartArrowsVertical
            class="icon"
            stroke-width="2"
            color="currentColor"
          /> 
          <h3 class="card-title">
            Vulnerabilities ({{ totalVulnerabilitiesCount }})
          </h3>
        </div>
        <div class="card-body">
          <div class="row justify-content-center">
            <div
              v-for="(stat, index) in overallStats"
              :key="index"
              class="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-2"
            >
              <div class="card card-sm no-border">
                <div class="card-body">
                  <div class="row align-items-center">
                    <div class="col-auto">
                      <span
                        :class="['avatar', getSeverityColorClass(stat.title)]"
                      >
                        <span class="text-white">{{ stat.value }}</span>
                      </span>
                    </div>
                    <div class="col">
                      <div>
                        <strong>{{ stat.title }}</strong>
                      </div>
                      <div class="text-secondary">vulnerabilities</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Patch Tuesday Alert Message -->
    <div
      v-if="patchTuesdayOnly && !isValidPatchTuesdayDate"
      class="container mt-3"
    >
      <Message
        severity="error"
        icon="pi pi-times-circle"
        class="mb-2"
        :closable="false"
      >
        Patch Tuesday hasn't happened yet for the current month, or data for the
        exact Patch Tuesday date is not available.
      </Message>
    </div>

    <!-- Threat Cards -->
    <div
      v-if="!patchTuesdayOnly || isValidPatchTuesdayDate"
      class="container mt-3"
    >
      <div class="row">
        <ThreatCard
          title="Threat Categories"
          icon-component-name="IconVirusSearch"
          :items="sortedCategoryCounts"
          :get-emoji="getEmojiForName"
        />
        <ThreatCard
          title="Affected Products"
          icon-component-name="IconBrandWindows"
          :items="sortedVulnerabilityCountsByName"
          :get-emoji="getEmojiForName"
        />
      </div>
    </div>

    <!-- Vulnerability Tables -->
    <div
      v-for="(tableData, index) in nonEmptyTablesData"
      :key="tableData.title"
      class="container mt-3"
    >
      <div class="card modern-card shadow-sm h-100">
        <div class="card-header">
          <component
            :is="tableData.icon"
            class="icon"
            stroke-width="2"
            color="currentColor"
          />
           
          <h5 class="card-title">
            {{ tableData.title }} ({{
              tableData.filteredVulnerabilities.length
            }})
          </h5>
        </div>
        <div class="card-body">
          <div
            class="d-flex justify-content-between mb-2 align-items-center flex-wrap"
          >
            <div class="input-group search-input-group mb-2 mb-md-0">
              <span class="input-group-text">
                <i class="pi pi-search"></i>
              </span>
              <InputText
                type="text"
                v-model="state.filters[getFilterKeyForTable(tableData.title)]"
                @input="
                  state.searchPerformed[
                    getFilterKeyForTable(tableData.title)
                  ] = true
                "
                placeholder="Keyword Search"
                class="p-inputtext-sm form-control"
              />
            </div>
            <Button
              label="Export CSV"
              icon="pi pi-download"
              iconPos="left"
              size="small"
              class="p-button-outlined export-button"
              @click="exportTableAsCSV(index)"
              :disabled="tableData.filteredVulnerabilities.length === 0"
            />
          </div>

          <DataTable
            ref="dataTableRefs"
            v-if="tableData.filteredVulnerabilities.length > 0"
            :value="tableData.filteredVulnerabilities"
            :removableSort="true"
            :paginator="true"
            :rows="10"
            :rowsPerPageOptions="[10, 20, 50]"
            responsiveLayout="scroll"
            :exportFilename="`patchpalooza-${selectedMonth}-${tableData.title
              .toLowerCase()
              .replace(' ', '-')}`"
            stateStorage="session"
            :stateKey="`datatable-${selectedMonth}-${tableData.title}`"
          >
            <Column
              field="CVE"
              header="CVE"
              :sortable="true"
              :style="{ width: '150px', 'white-space': 'nowrap' }"
            >
              <template #body="{ data }">
                <a
                  :href="
                    'https://msrc.microsoft.com/update-guide/vulnerability/' +
                    data.CVE
                  "
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {{ data.CVE }}
                </a>
              </template>
            </Column>

            <Column field="TitleValue" header="Title" :sortable="true">
              <template #body="{ data }">
                <div class="d-flex align-items-center">
                  <span class="title-cell-text">
                    {{ data.Title?.Value }}
                  </span>
                  <button
                    class="btn btn-link p-0"
                    @click="
                      openFaqDialog(
                        getFaqContent(data),
                        data.CVE,
                        data.Title.Value
                      )
                    "
                    title="More info"
                    style="flex-shrink: 0"
                    aria-label="Show FAQ"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="icon icon-tabler icon-tabler-info-hexagon"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      stroke-width="2"
                      stroke="currentColor"
                      fill="none"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                      <path
                        d="M19.875 6.27c.7 .398 1.13 1.143 1.125 1.948v7.284c0 .809 -.443 1.555 -1.158 1.948l-6.75 4.27a2.269 2.269 0 0 1 -2.184 0l-6.75 -4.27a2.225 2.225 0 0 1 -1.158 -1.948v-7.285c0 -.809 .443 -1.554 1.158 -1.947l6.75 -3.98a2.33 2.33 0 0 1 2.25 0l6.75 3.98h-.033z"
                      />
                      <path d="M12 9h.01" />
                      <path d="M11 12h1v4h1" />
                    </svg>
                  </button>
                </div>
              </template>
            </Column>

            <Column
              field="CVSS"
              header="CVSS"
              :sortable="true"
              :style="{ width: '120px', 'white-space': 'nowrap' }"
            >
              <template #body="{ data }">
                <span
                  :class="[
                    getComputedCvssData(data).severityClass,
                    'hover-pointer',
                  ]"
                  :title="getComputedCvssData(data).severityLevel"
                >
                  <template v-if="getComputedCvssData(data).score !== 'N/A'">
                    <span @click="openCvssDetailsDialog(data.CVSSScoreSets[0])">
                      {{ getComputedCvssData(data).score }}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="icon icon-tabler icon-tabler-list-details"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        stroke-width="2"
                        stroke="currentColor"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        style="vertical-align: middle"
                        aria-hidden="true"
                      >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M13 5h8" />
                        <path d="M13 9h5" />
                        <path d="M13 15h8" />
                        <path d="M13 19h5" />
                        <path
                          d="M3 4m0 1a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v4a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1z"
                        />
                        <path
                          d="M3 14m0 1a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v4a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1z"
                        />
                      </svg>
                    </span>
                  </template>
                  <template v-else>
                    {{ getComputedCvssData(data).score }}
                  </template>
                </span>
              </template>
            </Column>

            <Column
              field="Category"
              header="Threat Category"
              :sortable="true"
              :style="{ width: '180px', 'white-space': 'nowrap' }"
            >
              <template #body="{ data }">
                <span>{{ getThreatCategory(data) || "Unknown Category" }}</span>
              </template>
            </Column>

            <Column
              field="Product"
              header="Product"
              :sortable="true"
              :style="{ width: '200px', 'white-space': 'nowrap' }"
            >
              <template #body="{ data }">
                <span>{{ getProductName(data) }}</span>
              </template>
            </Column>
          </DataTable>

          <div
            v-if="
              tableData.filteredVulnerabilities.length === 0 &&
              state.searchPerformed[getFilterKeyForTable(tableData.title)]
            "
            class="alert alert-warning mt-2"
            role="alert"
          >
            No vulnerabilities found for the search criteria.
          </div>
        </div>
      </div>
    </div>

    <!-- FAQ Dialog -->
    <PVDialog
      v-model:visible="showFaqDialog"
      :header="dialogHeader"
      :style="{ width: '85vw' }"
      :breakpoints="{ '1199px': '75vw', '575px': '90vw' }"
      modal
    >
      <div v-html="selectedFaqContent"></div>
      <template #footer>
        <Button
          label="Close"
          icon="pi pi-times"
          @click="showFaqDialog = false"
          class="p-button-text"
        />
      </template>
    </PVDialog>

    <!-- CVSS Dialog -->
    <PVDialog
      v-model:visible="showCvssDialog"
      header="CVSS Details"
      :style="{ width: '50vw' }"
      :breakpoints="{ '991px': '75vw', '575px': '90vw' }"
      modal
    >
      <div v-if="selectedCvssScoreSet" class="cvss-details">
        <div class="cvss-vector-link">
          <p>
            <strong>Vector: </strong>
            <a
              :href="
                'https://www.first.org/cvss/calculator/3.1#' +
                selectedCvssScoreSet.Vector
              "
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ selectedCvssScoreSet.Vector }}
            </a>
          </p>
        </div>
        <br />
        <div class="cvss-scores-table">
          <table>
            <tbody>
              <tr>
                <td>Base Score:</td>
                <td>{{ selectedCvssScoreSet.BaseScore }}</td>
              </tr>
              <tr>
                <td>Temporal Score:</td>
                <td>{{ selectedCvssScoreSet.TemporalScore }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <br />
        <div class="cvss-vector-table">
          <table>
            <tbody>
              <tr
                v-for="(value, key) in parseCvssVectorString(
                  selectedCvssScoreSet.Vector
                )"
                :key="key"
              >
                <td>{{ key }}</td>
                <td :class="getCvssValueClass(key, value)">
                  {{ value }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <template #footer>
        <Button
          label="Close"
          icon="pi pi-times"
          @click="showCvssDialog = false"
          class="p-button-text"
        />
      </template>
    </PVDialog>
  </div>
</template>

<script>
// =============================
// Constants & Pure Helper Functions
// =============================
const CVSS_METRIC_MAP = {
  "AV:N": "Attack Vector: Network",
  "AV:A": "Attack Vector: Adjacent",
  "AV:L": "Attack Vector: Local",
  "AV:P": "Attack Vector: Physical",
  "AC:L": "Attack Complexity: Low",
  "AC:H": "Attack Complexity: High",
  "PR:N": "Privileges Required: None",
  "PR:L": "Privileges Required: Low",
  "PR:H": "Privileges Required: High",
  "UI:N": "User Interaction: None",
  "UI:R": "User Interaction: Required",
  "S:U": "Scope: Unchanged",
  "S:C": "Scope: Changed",
  "C:N": "Confidentiality: None",
  "C:L": "Confidentiality: Low",
  "C:H": "Confidentiality: High",
  "I:N": "Integrity: None",
  "I:L": "Integrity: Low",
  "I:H": "Integrity: High",
  "A:N": "Availability: None",
  "A:L": "Availability: Low",
  "A:H": "Availability: High",
  "E:U": "Exploit Code Maturity: Unproven",
  "E:P": "Exploit Code Maturity: Proof-of-Concept",
  "E:F": "Exploit Code Maturity: Functional",
  "E:H": "Exploit Code Maturity: High",
  "RL:O": "Remediation Level: Official Fix",
  "RL:T": "Remediation Level: Temporary Fix",
  "RL:W": "Remediation Level: Workaround",
  "RL:U": "Remediation Level: Unavailable",
  "RC:U": "Report Confidence: Unknown",
  "RC:R": "Report Confidence: Reasonable",
  "RC:C": "Report Confidence: Confirmed",
};

const THREAT_CATEGORIES_FOR_STATS = [
  "Elevation of Privilege",
  "Security Feature Bypass",
  "Remote Code Execution",
  "Information Disclosure",
  "Denial of Service",
  "Spoofing",
  "Edge - Chromium",
];

const EMOJI_MAP = {
  "Edge - Chromium": "🌐",
  "Remote Code Execution": "🎯",
  "Elevation of Privilege": "🔝",
  "Information Disclosure": "🔍",
  "Security Feature Bypass": "🛡️",
  "Denial of Service": "⛔",
  Spoofing: "🎭",
  Windows: "🪟",
  Browser: "🌐",
  ESU: "⏳",
  Azure: "☁️",
  Apps: "🛍️",
  "Developer Tools": "🛠️",
  "Microsoft Office": "📊",
  "Exchange Server": "📧",
  "SQL Server": "🗄️",
  "System Center": "🧰",
  Mariner: "🚢",
  "Microsoft Dynamics": "♾️",
  Other: "⚙️",
  Device: "💻",
  "Open Source Software": "🐧",
  "Server Software": "🖥️",
};

const SEVERITY_COLOR_MAP = {
  Critical: "bg-red",
  High: "bg-orange",
  Medium: "bg-yellow",
  Low: "bg-green",
  "N/A": "bg-azure",
};

/**
 * Calculates the UTC day number (1-31) of the second Tuesday of a given UTC month and year.
 * @param {number} utcYear - The full year (e.g., 2023).
 * @param {number} utcMonth_0_indexed - The UTC month, 0-indexed (0 for January, 11 for December).
 * @returns {number} The UTC day number (1-31) of the second Tuesday.
 */
function getSecondTuesdayUTCDayNumber(utcYear, utcMonth_0_indexed) {
  const firstOfMonthUTC = new Date(Date.UTC(utcYear, utcMonth_0_indexed, 1));
  const dayOfWeekForFirstUTC = firstOfMonthUTC.getUTCDay(); // 0 (Sunday) to 6 (Saturday)
  let daysToAddForFirstTuesday = (2 - dayOfWeekForFirstUTC + 7) % 7;
  return 1 + daysToAddForFirstTuesday + 7;
}

function sortSummariesByMonthYear(summaries) {
  const monthsOrder = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return summaries.sort((a, b) => {
    const [yearA, monthA] = a.ID.split("-");
    const [yearB, monthB] = b.ID.split("-");
    if (yearA !== yearB) return parseInt(yearB) - parseInt(yearA);
    return monthsOrder.indexOf(monthB) - monthsOrder.indexOf(monthA);
  });
}

function parseCvssVectorStringToMap(vectorString, metricMap = CVSS_METRIC_MAP) {
  if (!vectorString || !vectorString.startsWith("CVSS:")) return {};
  return vectorString
    .split("/")
    .slice(1)
    .reduce((parsed, metric) => {
      const [abbrev, val] = metric.split(":");
      const full = metricMap[`${abbrev}:${val}`] || `${abbrev}:${val}`;
      const idx = full.lastIndexOf(":");
      parsed[full.substring(0, idx)] = full.substring(idx + 1).trim();
      return parsed;
    }, {});
}

// =============================
// Vue Component Definition
// =============================
import {
  IconBrandWindows,
  IconCalendar,
  IconChartArrowsVertical,
  IconVirusSearch,
  IconBiohazard,
  IconBiohazardOff,
} from "@tabler/icons-vue";
import { reactive } from "vue";
import InputText from "primevue/inputtext";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import Button from "primevue/button";
import PrimeVueDialog from "primevue/dialog";
import ToggleSwitch from "primevue/toggleswitch";
import Message from "primevue/message";

import ThreatCard from "@/components/ThreatCard.vue";
import config from "@/config";

export default {
  name: "VulnerabilityDashboard",
  components: {
    InputText,
    Column,
    DataTable,
    Button,
    PVDialog: PrimeVueDialog,
    ToggleSwitch,
    Message,
    IconBrandWindows,
    IconCalendar,
    IconChartArrowsVertical,
    IconVirusSearch,
    IconBiohazard,
    IconBiohazardOff,
    ThreatCard,
  },
  props: {
    isPatchTuesdayPage: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const state = reactive({
      filters: { exploited: "", notExploited: "" },
      searchPerformed: { exploited: false, notExploited: false },
    });
    return { state };
  },
  data() {
    return {
      isLoading: true,
      documentTitle: "Loading...",
      patchTuesdayOnly: false,

      apiData: null,
      vulnerabilities: [],
      summaries: [],

      patchTuesdayDate: null,
      isValidPatchTuesdayDate: true,

      productCategoryMap: {},
      vulnerabilityCountsByName: {},
      categoryCounts: {},
      severityCounts: { Critical: 0, High: 0, Medium: 0, Low: 0, "N/A": 0 },
      totalVulnerabilities: 0,

      selectedMonth: "",

      showFaqDialog: false,
      selectedFaqContent: "",
      dialogHeader: "",
      showCvssDialog: false,
      selectedCvssScoreSet: null,

      BASE_URL: config.BASE_URL,
    };
  },
  computed: {
    filteredSummaries() {
      return Array.isArray(this.summaries)
        ? this.summaries.filter((summary) => summary.ID !== "2017-May-B")
        : [];
    },
    vulnerabilitiesByExploitationStatus() {
      const byStatus = { Exploited: [], "Not Exploited": [] };
      if (!this.vulnerabilities) return byStatus;

      this.vulnerabilities.forEach((vuln) => {
        const statusKey = this.getExploitationStatus(vuln);
        byStatus[statusKey].push(vuln);
      });
      return {
        exploited: this.sortVulnerabilitiesForDisplay(byStatus.Exploited),
        notExploited: this.sortVulnerabilitiesForDisplay(
          byStatus["Not Exploited"]
        ),
      };
    },
    vulnerabilityTablesDefinition() {
      return [
        {
          title: "Exploited",
          icon: IconBiohazard,
          vulnerabilities: this.vulnerabilitiesByExploitationStatus.exploited,
        },
        {
          title: "Not Exploited",
          icon: IconBiohazardOff,
          vulnerabilities:
            this.vulnerabilitiesByExploitationStatus.notExploited,
        },
      ];
    },
    filteredAndProcessedTablesData() {
      return this.vulnerabilityTablesDefinition.map((tableDef) => {
        const filterKey = this.getFilterKeyForTable(tableDef.title);
        const filterText = (this.state.filters[filterKey] || "").toLowerCase();

        const filteredVulnerabilities = tableDef.vulnerabilities.filter(
          (vuln) => this.doesVulnerabilityMatchFilter(vuln, filterText)
        );
        return {
          ...tableDef,
          filteredVulnerabilities,
          hasResults: filteredVulnerabilities.length > 0,
        };
      });
    },
    nonEmptyTablesData() {
      return this.filteredAndProcessedTablesData.filter((tableData) => {
        const filterKey = this.getFilterKeyForTable(tableData.title);
        return (
          tableData.hasResults ||
          (this.state.filters[filterKey] &&
            this.state.searchPerformed[filterKey])
        );
      });
    },
    sortedVulnerabilityCountsByName() {
      return Object.entries(this.vulnerabilityCountsByName || {})
        .sort((a, b) => b[1] - a[1])
        .map(([name, count]) => ({ name, count }));
    },
    overallStats() {
      return Object.keys(this.severityCounts).map((title) => ({
        title,
        value: this.severityCounts[title],
      }));
    },
    totalVulnerabilitiesCount() {
      return this.totalVulnerabilities;
    },
    sortedCategoryCounts() {
      return Object.entries(this.categoryCounts || {})
        .filter(([, count]) => count > 0)
        .sort((a, b) => b[1] - a[1])
        .map(([category, count]) => ({ name: category, count }));
    },
  },
  watch: {
    patchTuesdayOnly() {
      if (this.apiData) {
        this.vulnerabilities = this.filterVulnerabilitiesByPatchTuesday(
          this.apiData
        );
        this.processAndComputeAllData();
      }
    },
  },
  async mounted() {
    this.isLoading = true;
    await this.fetchMonthlySummaries();
    if (this.filteredSummaries.length > 0) {
      this.selectedMonth = this.filteredSummaries[0].ID;
      await this.fetchVulnerabilityDataForMonth(this.selectedMonth);
    } else {
      this.documentTitle = "No data available";
      this.isLoading = false;
    }
  },
  methods: {
    // --- API Fetching Methods ---
    async fetchMonthlySummaries() {
      try {
        const response = await fetch(`${this.BASE_URL}updates`, {
          headers: { Accept: "application/json" },
        });
        if (!response.ok)
          throw new Error(
            `Failed to fetch summaries (status: ${response.status})`
          );
        const responseData = await response.json();
        this.summaries = sortSummariesByMonthYear(responseData.value || []);
      } catch (error) {
        console.error("Error fetching summaries:", error);
        this.summaries = [];
      }
    },
    async retrieveDataForSelectedMonth() {
      if (this.selectedMonth) {
        await this.fetchVulnerabilityDataForMonth(this.selectedMonth);
      }
    },
    async fetchVulnerabilityDataForMonth(monthId) {
      this.isLoading = true;
      try {
        const response = await fetch(`${this.BASE_URL}cvrf/${monthId}`, {
          headers: { Accept: "application/json" },
        });
        if (!response.ok)
          throw new Error(
            `Failed to fetch data for ${monthId} (status: ${response.status})`
          );
        this.apiData = await response.json();

        this.documentTitle =
          this.apiData.DocumentTitle?.Value || `Vulnerabilities for ${monthId}`;
        // Call filterVulnerabilitiesByPatchTuesday first as it sets isValidPatchTuesdayDate
        this.vulnerabilities = this.filterVulnerabilitiesByPatchTuesday(
          this.apiData
        );
        this.processAndComputeAllData();
      } catch (error) {
        console.error(`Error fetching data for month ${monthId}:`, error);
        this.apiData = null;
        this.vulnerabilities = [];
        this.documentTitle = `Error loading data for ${monthId}`;
        this.processAndComputeAllData();
      } finally {
        this.isLoading = false;
      }
    },

    // --- Data Processing & State Update Methods ---
    processAndComputeAllData() {
      this.productCategoryMap = this.buildProductToCategoryMap(this.apiData);
      this.enrichVulnerabilityObjects();
      this.computeAggregatedStatistics();
      this.computeProductVulnerabilityCounts();
    },
    checkIfValidPatchTuesdayRelease(dateString) {
      if (!dateString) return false;
      const releaseDate = new Date(dateString);

      const releaseDate_UTCDate = releaseDate.getUTCDate();
      const releaseDate_UTCMonth = releaseDate.getUTCMonth();
      const releaseDate_UTCYear = releaseDate.getUTCFullYear();

      const calculatedSecondTuesdayDayNumber = getSecondTuesdayUTCDayNumber(
        releaseDate_UTCYear,
        releaseDate_UTCMonth
      );

      return releaseDate_UTCDate === calculatedSecondTuesdayDayNumber;
    },
    filterVulnerabilitiesByPatchTuesday(apiData) {
      if (!apiData || !apiData.Vulnerability) {
        this.isValidPatchTuesdayDate = !this.patchTuesdayOnly;
        return [];
      }

      if (!this.patchTuesdayOnly) {
        this.isValidPatchTuesdayDate = true;
        return apiData.Vulnerability;
      }

      const docInitialReleaseDate =
        apiData.DocumentTracking?.InitialReleaseDate;

      if (
        docInitialReleaseDate &&
        this.checkIfValidPatchTuesdayRelease(docInitialReleaseDate)
      ) {
        this.isValidPatchTuesdayDate = true;
        this.patchTuesdayDate = docInitialReleaseDate;

        return apiData.Vulnerability.filter((vuln) =>
          vuln.RevisionHistory?.some(
            (revision) => revision.Date === this.patchTuesdayDate
          )
        );
      } else {
        this.isValidPatchTuesdayDate = false;
        this.patchTuesdayDate = null;
        return [];
      }
    },
    enrichVulnerabilityObjects() {
      if (!this.vulnerabilities) {
        this.vulnerabilities = [];
        return;
      }
      this.vulnerabilities = this.vulnerabilities.map((vuln) => ({
        ...vuln,
        TitleValue: vuln.Title?.Value || "N/A",
        CVSS: this.getComputedCvssData(vuln).score,
        Category: this.getThreatCategory(vuln) || "Unknown Category",
        Product: this.getProductName(vuln),
      }));
    },
    doesVulnerabilityMatchFilter(vuln, filterText) {
      if (!filterText) return true;
      const match = (text) =>
        typeof text === "string" && text.toLowerCase().includes(filterText);
      return (
        match(vuln.CVE) ||
        match(vuln.TitleValue) ||
        match(vuln.Category) ||
        match(vuln.Product) ||
        match(vuln.CVSS)
      );
    },
    sortVulnerabilitiesForDisplay(vulns) {
      return [...vulns].sort((a, b) => {
        const aExploited = this.getExploitationStatus(a) === "Exploited";
        const bExploited = this.getExploitationStatus(b) === "Exploited";
        if (aExploited !== bExploited) return aExploited ? -1 : 1;

        const aScore = this.getBaseCvssScore(a);
        const bScore = this.getBaseCvssScore(b);
        if (aScore === "N/A" && bScore !== "N/A") return 1;
        if (bScore === "N/A" && aScore !== "N/A") return -1;
        if (aScore === "N/A" && bScore === "N/A") return 0;

        return parseFloat(bScore) - parseFloat(aScore);
      });
    },
    buildProductToCategoryMap(apiData) {
      const map = {};
      if (!apiData || !apiData.ProductTree?.Branch) return map;

      apiData.ProductTree.Branch.forEach((branch) => {
        (branch.Items || []).forEach((item) => {
          (item.Items || []).forEach((product) => {
            if (product.ProductID && product.Value) {
              map[product.ProductID] = item.Name;
            }
          });
        });
      });
      return map;
    },
    computeAggregatedStatistics() {
      if (!this.vulnerabilities) {
        this.totalVulnerabilities = 0;
        this.severityCounts = {
          Critical: 0,
          High: 0,
          Medium: 0,
          Low: 0,
          "N/A": 0,
        };
        this.categoryCounts = THREAT_CATEGORIES_FOR_STATS.reduce(
          (acc, cat) => ({ ...acc, [cat]: 0 }),
          {}
        );
        return;
      }
      this.totalVulnerabilities = this.vulnerabilities.length;

      this.severityCounts = this.vulnerabilities.reduce(
        (acc, vuln) => {
          const { severityLevel } = this.getComputedCvssData(vuln);
          acc[severityLevel] = (acc[severityLevel] || 0) + 1;
          return acc;
        },
        { Critical: 0, High: 0, Medium: 0, Low: 0, "N/A": 0 }
      );

      this.categoryCounts = THREAT_CATEGORIES_FOR_STATS.reduce(
        (acc, category) => {
          acc[category] = this.vulnerabilities.filter((vuln) => {
            if (!vuln.Threats) return false;
            return vuln.Threats.some(
              (threat) =>
                threat.Type === 0 &&
                (threat.Description?.Value === category ||
                  (category === "Edge - Chromium" &&
                    threat.ProductID?.includes("11655")))
            );
          }).length;
          return acc;
        },
        {}
      );
    },
    computeProductVulnerabilityCounts() {
      if (!this.vulnerabilities) {
        this.vulnerabilityCountsByName = {};
        return;
      }
      this.vulnerabilityCountsByName = this.vulnerabilities.reduce(
        (acc, vulnerability) => {
          const uniqueProductCategories = new Set();
          (vulnerability.ProductStatuses || []).forEach((productStatus) => {
            (productStatus.ProductID || []).forEach((pid) => {
              if (this.productCategoryMap[pid]) {
                uniqueProductCategories.add(this.productCategoryMap[pid]);
              }
            });
          });
          uniqueProductCategories.forEach((categoryName) => {
            acc[categoryName] = (acc[categoryName] || 0) + 1;
          });
          return acc;
        },
        {}
      );
    },

    // --- Vulnerability Data Extraction Helpers ---
    getExploitationStatus(vuln) {
      if (Array.isArray(vuln.Threats)) {
        const isExploited = vuln.Threats.some((threat) =>
          threat?.Description?.Value?.toLowerCase().includes("exploited:yes")
        );
        return isExploited ? "Exploited" : "Not Exploited";
      }
      return "Not Exploited";
    },
    getBaseCvssScore(vuln) {
      return vuln.CVSSScoreSets?.[0]?.BaseScore || "N/A";
    },
    determineCvssSeverity(cvssScoreString) {
      if (cvssScoreString === "N/A") {
        return { class: "badge badge-outline text-azure", level: "N/A" };
      }
      const score = parseFloat(cvssScoreString);
      if (score >= 9.0)
        return { class: "badge badge-outline text-red", level: "Critical" };
      if (score >= 7.0)
        return { class: "badge badge-outline text-orange", level: "High" };
      if (score >= 4.0)
        return { class: "badge badge-outline text-yellow", level: "Medium" };
      if (score > 0.0)
        return { class: "badge badge-outline text-green", level: "Low" };
      return { class: "badge badge-outline text-azure", level: "N/A" };
    },
    getComputedCvssData(vuln) {
      const score = this.getBaseCvssScore(vuln);
      const severityInfo = this.determineCvssSeverity(score);
      return {
        score: score,
        severityClass: severityInfo.class,
        severityLevel: severityInfo.level,
      };
    },
    getProductName(vuln) {
      if (vuln.ProductStatuses?.length > 0) {
        const firstProductId = vuln.ProductStatuses[0].ProductID?.[0];
        if (firstProductId && this.productCategoryMap[firstProductId]) {
          return this.productCategoryMap[firstProductId];
        }
      }
      return "N/A";
    },
    getFaqContent(vuln) {
      const faqEntries =
        vuln.Notes?.filter((note) => note.Title === "FAQ") || [];
      return faqEntries.length === 0
        ? "<p>No FAQ available for this vulnerability.</p>"
        : faqEntries
            .map((faq) => `<p>${faq.Value.replace(/\n/g, "<br>")}</p>`)
            .join("<hr class='my-2'>"); // Added newline to <br>
    },
    getThreatCategory(vuln) {
      return (
        vuln.Threats?.find((threat) => threat.Type === 0)?.Description.Value ||
        "N/A"
      );
    },

    // --- UI Interaction & Dialog Methods ---
    openFaqDialog(faqContent, cve, title) {
      this.selectedFaqContent = faqContent;
      this.dialogHeader = `FAQ: ${cve} - ${title}`;
      this.showFaqDialog = true;
    },
    openCvssDetailsDialog(cvssScoreSet) {
      this.selectedCvssScoreSet = cvssScoreSet;
      this.showCvssDialog = true;
    },
    parseCvssVectorString(vectorString) {
      return parseCvssVectorStringToMap(vectorString, CVSS_METRIC_MAP);
    },
    getCvssValueClass(metricKey, metricValue) {
      if (metricKey === "Attack Vector" && metricValue === "Network")
        return "cvss-critical";
      if (metricKey === "Attack Complexity" && metricValue === "Low")
        return "cvss-critical";
      if (metricKey === "Privileges Required" && metricValue === "None")
        return "cvss-critical";
      return "";
    },
    exportTableAsCSV(tableIndex) {
      const dataTableComponent =
        this.$refs.dataTableRefs && this.$refs.dataTableRefs[tableIndex];
      if (
        dataTableComponent &&
        typeof dataTableComponent.exportCSV === "function"
      ) {
        dataTableComponent.exportCSV();
      } else {
        console.error(
          "DataTable reference or exportCSV method not found for index:",
          tableIndex
        );
      }
    },

    // --- UI Display Helpers ---
    getEmojiForName(name) {
      return EMOJI_MAP[name] || "❓";
    },
    getSeverityColorClass(severityTitle) {
      return SEVERITY_COLOR_MAP[severityTitle] || "bg-secondary";
    },
    getFilterKeyForTable(tableTitle) {
      return tableTitle === "Exploited" ? "exploited" : "notExploited";
    },
  },
};
</script>

<style scoped>
.loader-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  flex-direction: column;
}
.modern-card {
  border: none;
  background-color: #ffffff;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}
.form-select {
  width: auto;
  display: inline-block;
}

.cvss-details table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1em;
  table-layout: fixed;
}
.cvss-details th,
.cvss-details td {
  padding: 0.5em;
  border: 1px solid #dee2e6;
  text-align: left;
}
.cvss-details th:first-child,
.cvss-details td:first-child {
  font-weight: bold;
  background-color: #f8f9fa;
  width: 40%;
}
.cvss-vector-link p {
  margin: 0.5em 0;
}
.cvss-vector-link a {
  color: #007bff;
  text-decoration: none;
}
.cvss-vector-link a:hover {
  text-decoration: underline;
}
.hover-pointer:hover {
  cursor: pointer;
}
.cvss-critical {
  background-color: #fdd;
  color: #721c24;
  font-weight: bold;
}

.no-border {
  border: none !important;
  box-shadow: none !important;
}

.search-input-group {
  min-width: 250px;
  max-width: 350px;
}
.input-group-text {
  background-color: #fff;
  border-right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.form-control {
  border-left: 0;
  box-shadow: none;
}
.input-group .form-control {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-color: #ced4da;
}
.input-group .form-control:focus {
  border-color: #86b7fe;
  box-shadow: none;
  outline: none;
}

.title-cell-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 12px;
  flex-grow: 1;
}

@media (max-width: 767px) {
  .d-flex.justify-content-between.align-items-center {
    flex-direction: column;
    align-items: stretch !important;
  }
  .search-input-group {
    width: 100%;
    max-width: none;
  }
  .export-button {
    width: 100%;
    margin-top: 0.5rem;
  }
}
</style>
